import Factory from '@/Factories/Factory';
import type DetailViewContext from '@/ViewModels/Contexts/DetailViewContext';
import LayoutFactoryTuple from '@/Factories/LayoutFactoryTuple';
import { ITupleProvider } from '#/DefaultViewExtensionInterfaces';
import ControlFactoryTuple from '@/Factories/ControlFactoryTuple';
import GroupFactoryTuple from '@/Factories/GroupFactoryTuple';
import { ILayoutFactoryConstructorParameter } from '#/CoreFrameInterfaces';
import AsyncTypeGuards from '@/Utility/AsyncTypeGuards';
import type Context from '@/ViewModels/Context';

/**
* Repräsentiert die Oberklasse aller LayoutFactories
*/
export default class LayoutFactory extends Factory implements ITupleProvider<LayoutFactoryTuple> {

    /** 
     * Der Kontext aus dem die Factory erzeugt wurde
     */
    public context: DetailViewContext;

    /** 
     * Beinhaltet die Tuples, die gerendert werden sollen
     */
    public tuples: Array<ControlFactoryTuple | GroupFactoryTuple>;

    public tuple: LayoutFactoryTuple;

    constructor(params: ILayoutFactoryConstructorParameter) {
        super(params);
        this.isLayoutFactory = true;

        this.context = this.paramOrDefault(params.context, null);
        this.tuples = this.paramOrDefault(params.tuples, []);
        this.isDisposable = true;
        this.undisposeProperties.push('context');
    }

    public static async createInstanceAsync<T extends ILayoutFactoryConstructorParameter>(
        layout: string,
        context: Context,
        tuples: (ControlFactoryTuple | GroupFactoryTuple)[],
        additionalOptions: T
    ): Promise<{ layoutFactory?: LayoutFactory, tuple?: LayoutFactoryTuple }> {
        if (context?.isDisposed !== false)
            return {};

        const layoutFactory = Factory.createInstance<LayoutFactory>(layout, {
            tuples,
            context,
            ...additionalOptions
        }, 'DummyLayout');
        const tuple = AsyncTypeGuards.isAsyncTupleProvider<LayoutFactoryTuple>(layoutFactory)
            ? await layoutFactory.getTupleAsync()
            : layoutFactory.getTuple();

        if (!context.isDisposed) {
            context.setCreatedFactory(layoutFactory);
        }

        return {layoutFactory, tuple};
    }

    /**
     * Erzeugt das FactoryTuple und die Factory
     */
    public getTuple() {
        if (this.tuple)
            return this.tuple;

        const $control = this.getControl();
        this.tuple = new LayoutFactoryTuple(this, $control);
        return this.tuple;
    }

    getControl(): JQuery<HTMLElement> {
        return undefined;
    }
}